import { Navigate, useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiExternalLink,
  FiFileText,
  FiMessageSquare,
  FiGrid,
  FiMenu
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { blogApi, blogSite, readSession } from './api';
import PostEditor, { emptyPost } from './PostEditor';
import './blog.css';
function Taxonomy({ terms, reload }) {
  const [kind, setKind] = useState('category'),
    [name, setName] = useState(''),
    [edit, setEdit] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const action = async (path, method, body) => {
    setBusy(true);
    setError('');
    try {
      await blogApi(path, { method, body });
      setName('');
      setEdit(null);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="blog-panel">
      <h2>Categories & tags</h2>
      <p className="blog-muted">Organize your articles with categories and tags.</p>
      {error && (
        <div className="blog-alert error" role="alert">
          {error}
        </div>
      )}
      <form
        className="blog-taxonomy-form"
        onSubmit={(e) => {
          e.preventDefault();
          action('/admin/terms', 'POST', { kind, locale: 'en', name });
        }}
      >
        <label className="blog-field">
          Type
          <select aria-label="Type" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="category">Category</option>
            <option value="tag">Tag</option>
          </select>
        </label>
        <label className="blog-field">
          Name
          <input
            required
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Company news"
          />
        </label>
        <button className="blog-button" disabled={busy}>
          Add {kind}
        </button>
      </form>
      <div className="blog-table-wrap">
        <table className="blog-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {terms.map((term) => (
              <tr key={term._id}>
                <td>
                  {edit?.id === term._id ? (
                    <input
                      aria-label="Updated name"
                      value={edit.name}
                      onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                    />
                  ) : (
                    term.name
                  )}
                </td>
                <td>{term.kind}</td>
                <td>
                  <div className="blog-actions">
                    {edit?.id === term._id ? (
                      <>
                        <button
                          className="blog-button secondary"
                          disabled={busy}
                          onClick={() =>
                            action('/admin/terms/' + term._id, 'PUT', { name: edit.name })
                          }
                        >
                          Save
                        </button>
                        <button className="blog-button ghost" onClick={() => setEdit(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="blog-button ghost"
                        onClick={() => setEdit({ id: term._id, name: term.name })}
                      >
                        Rename
                      </button>
                    )}
                    <button
                      className="blog-button ghost danger"
                      disabled={busy}
                      onClick={() => {
                        if (window.confirm('Delete this unused category or tag?'))
                          action('/admin/terms/' + term._id, 'DELETE');
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!terms.length && (
          <div className="blog-empty">No categories or tags yet. Add your first topic above.</div>
        )}
      </div>
    </section>
  );
}
function Comments() {
  const [status, setStatus] = useState('pending'),
    [page, setPage] = useState(1),
    [result, setResult] = useState({ comments: [], totalPages: 1 }),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(''),
    [revision, setRevision] = useState(0),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    blogApi(`/admin/comments?status=${status}&page=${page}`, { signal: controller.signal })
      .then(setResult)
      .catch((e) => {
        if (e.name !== 'AbortError') setError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [status, page, revision]);
  const moderate = async (id, status) => {
    setBusy(true);
    setError('');
    try {
      await blogApi('/admin/comments/' + id, { method: 'PATCH', body: { status } });
      setRevision((n) => n + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="blog-panel">
      <div className="blog-section-heading">
        <div>
          <h2>Conversation, thoughtfully managed</h2>
          <p className="blog-muted">
            Only approved comments appear publicly. Email addresses are never shown.
          </p>
        </div>
        <select
          aria-label="Comment status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="pending">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All comments</option>
        </select>
      </div>
      {error && (
        <p className="blog-alert error" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        <p role="status">Loading comments…</p>
      ) : (
        result.comments.map((c) => (
          <article className="blog-comment" key={c._id}>
            <div className="blog-section-heading">
              <strong>{c.name}</strong>
              <span className={`blog-status ${c.status}`}>{c.status}</span>
            </div>
            <p>{c.body}</p>
            <small className="blog-muted">
              On {c.post?.title || 'Deleted article'} · {new Date(c.createdAt).toLocaleString()}
            </small>
            <div className="blog-actions">
              <button
                className="blog-button secondary"
                disabled={busy || c.status === 'approved'}
                onClick={() => moderate(c._id, 'approved')}
              >
                Approve
              </button>
              <button
                className="blog-button ghost danger"
                disabled={busy || c.status === 'rejected'}
                onClick={() => moderate(c._id, 'rejected')}
              >
                Reject
              </button>
              <button
                className="blog-button ghost"
                disabled={busy || c.status === 'pending'}
                onClick={() => moderate(c._id, 'pending')}
              >
                Hold for review
              </button>
            </div>
          </article>
        ))
      )}
      {!loading && !result.comments.length && (
        <div className="blog-empty">No {status === 'all' ? '' : status} comments.</div>
      )}
      <Pagination page={page} totalPages={result.totalPages} setPage={setPage} />
    </section>
  );
}
function Pagination({ page, totalPages, setPage }) {
  return (
    <div className="blog-pagination">
      <button
        className="blog-button secondary"
        disabled={page <= 1}
        onClick={() => setPage((p) => p - 1)}
      >
        Previous
      </button>
      <span>
        Page {page} of {totalPages}
      </span>
      <button
        className="blog-button secondary"
        disabled={page >= totalPages}
        onClick={() => setPage((p) => p + 1)}
      >
        Next
      </button>
    </div>
  );
}
export default function BlogAdmin({ Layout }) {
  const location = useLocation();
  const [session, setSession] = useState(readSession),
    [tab, setTab] = useState('articles'),
    [editor, setEditor] = useState(null);
  const [result, setResult] = useState({ posts: [], total: 0, totalPages: 1 }),
    [terms, setTerms] = useState([]),
    [authors, setAuthors] = useState([]),
    [suggestions, setSuggestions] = useState([]);
  const [search, setSearch] = useState(''),
    [query, setQuery] = useState(''),
    [status, setStatus] = useState(''),
    [category, setCategory] = useState(''),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState([]),
    [bulk, setBulk] = useState(''),
    [bulkCategory, setBulkCategory] = useState('');
  const [loading, setLoading] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [revision, setRevision] = useState(0);
  useEffect(() => {
    const expired = () => {
      setSession(null);
      setEditor(null);
    };
    window.addEventListener('blog-session-expired', expired);
    return () => window.removeEventListener('blog-session-expired', expired);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const reloadTerms = useCallback(async () => {
    const data = await blogApi('/admin/terms');
    setTerms(data);
  }, []);
  useEffect(() => {
    if (!session) return;
    let active = true;
    Promise.all([blogApi('/admin/terms'), blogApi('/admin/authors')])
      .then(([t, a]) => {
        if (active) {
          setTerms(t);
          setAuthors(a);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [session]);
  useEffect(() => {
    if (!session) return;
    const controller = new AbortController();
    setLoading(true);
    setSelected([]);
    setError('');
    const params = new URLSearchParams({
      page,
      limit: 12,
      search: query,
      status,
      category
    });
    blogApi('/admin/posts?' + params, { signal: controller.signal })
      .then(setResult)
      .catch((e) => {
        if (e.name !== 'AbortError') setError(e.message);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [session, query, status, category, page, revision]);
  useEffect(() => {
    if (!session || !editor) return;
    const controller = new AbortController();
    blogApi('/admin/posts?status=published&limit=100', { signal: controller.signal })
      .then((data) => setSuggestions(data.posts))
      .catch(() => {});
    return () => controller.abort();
  }, [session, editor]);
  const openEditor = async (id) => {
    setBusy(true);
    setError('');
    try {
      setEditor(await blogApi('/admin/posts/' + id));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const applyBulk = async () => {
    if (!selected.length || !bulk) return;
    if (
      bulk === 'delete' &&
      !window.confirm(`Move ${selected.length} article(s) to Trash? Public access will stop.`)
    )
      return;
    setBusy(true);
    setError('');
    try {
      const data = await blogApi('/admin/bulk', {
        method: 'POST',
        body: { ids: selected, action: bulk, categoryId: bulkCategory }
      });
      toast.success(`${data.updated} article(s) updated.`);
      setSelected([]);
      setRevision((n) => n + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Layout
      title_props={<h1 className="text-xl font-semibold">Blog management</h1>}
      ToggleBtn={
        <button type="button" aria-label="Open navigation" className="flex items-center gap-3">
          <FiMenu size={22} />
          <span>Blog management</span>
        </button>
      }
    >
      <div className="blog-admin">
        {!session ? (
          <Navigate
            to="/login"
            replace
            state={{ reauthenticate: true, returnTo: location.pathname }}
          />
        ) : editor ? (
          <PostEditor
            key={editor._id || editor.translationGroup || 'new'}
            initial={editor}
            authors={authors}
            terms={terms}
            suggestions={suggestions}
            onBack={() => {
              setEditor(null);
              setRevision((n) => n + 1);
            }}
            onSaved={(saved) => {
              setRevision((n) => n + 1);
              if (saved.status === 'published') {
                setEditor(null);
                setTab('articles');
                setSearch('');
                setQuery('');
                setStatus('');
                setCategory('');
                setPage(1);
                toast.success('Article published successfully.');
              }
            }}
          />
        ) : (
          <>
            <div className="blog-heading">
              <div>
                <p className="blog-muted">Welcome Habibi publishing desk</p>
                <h2>Stories worth sharing.</h2>
                <p className="blog-muted">Create and publish from one place.</p>
              </div>
              <div className="blog-actions">
                <a
                  className="blog-button secondary"
                  href={blogSite + '/blog'}
                  target="_blank"
                  rel="noreferrer"
                >
                  <FiExternalLink /> View blog
                </a>
                <button className="blog-button" onClick={() => setEditor(emptyPost('en'))}>
                  <FiPlus /> New article
                </button>
              </div>
            </div>
            <div className="blog-tabs" role="tablist" aria-label="Blog management sections">
              {[
                ['articles', FiFileText, 'Articles'],
                ['taxonomy', FiGrid, 'Categories & tags'],
                ['comments', FiMessageSquare, 'Comments']
              ].map(([key, Icon, label]) => (
                <button
                  role="tab"
                  aria-selected={tab === key}
                  key={key}
                  onClick={() => setTab(key)}
                >
                  <Icon /> {label}
                </button>
              ))}
            </div>
            {error && (
              <div className="blog-alert error" role="alert">
                {error}
              </div>
            )}
            {tab === 'taxonomy' && <Taxonomy terms={terms} reload={reloadTerms} />}
            {tab === 'comments' && <Comments />}
            {tab === 'articles' && (
              <section className="blog-panel">
                <div className="blog-filter-bar">
                  <div className="blog-search">
                    <FiSearch />
                    <input
                      aria-label="Search articles"
                      placeholder="Search by title or URL…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <select
                    aria-label="Filter status"
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All articles</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="published">Published</option>
                    <option value="trash">Trash</option>
                  </select>
                  <select
                    aria-label="Filter category"
                    value={category}
                    onChange={(e) => {
                      setCategory(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All categories</option>
                    {terms
                      .filter((t) => t.kind === 'category')
                      .map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="blog-bulk-bar">
                  <select
                    aria-label="Bulk action"
                    value={bulk}
                    onChange={(e) => setBulk(e.target.value)}
                  >
                    <option value="">Bulk actions</option>
                    {status === 'trash' ? (
                      <option value="restore">Restore as draft</option>
                    ) : (
                      <>
                        <option value="publish">Publish</option>
                        <option value="unpublish">Unpublish</option>
                        <option value="category">Change category</option>
                        <option value="delete">Move to Trash</option>
                      </>
                    )}
                  </select>
                  {bulk === 'category' && (
                    <select
                      aria-label="Bulk category"
                      value={bulkCategory}
                      onChange={(e) => setBulkCategory(e.target.value)}
                    >
                      <option value="">Choose category</option>
                      {terms
                        .filter((t) => t.kind === 'category')
                        .map((t) => (
                          <option key={t._id} value={t._id}>
                            {t.name}
                          </option>
                        ))}
                    </select>
                  )}
                  <button
                    className="blog-button secondary"
                    disabled={
                      busy || !bulk || !selected.length || (bulk === 'category' && !bulkCategory)
                    }
                    onClick={applyBulk}
                  >
                    Apply
                  </button>
                  <span className="blog-muted">
                    {selected.length ? selected.length + ' selected' : result.total + ' articles'}
                  </span>
                </div>
                <div className="blog-table-wrap">
                  <table className="blog-table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            aria-label="Select all articles on this page"
                            checked={
                              !!result.posts.length && selected.length === result.posts.length
                            }
                            onChange={(e) =>
                              setSelected(e.target.checked ? result.posts.map((p) => p._id) : [])
                            }
                          />
                        </th>
                        <th>Article</th>
                        <th>Status</th>
                        <th>Author</th>
                        <th>Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading &&
                        result.posts.map((post) => (
                          <tr key={post._id}>
                            <td>
                              <input
                                type="checkbox"
                                aria-label={'Select ' + post.title}
                                checked={selected.includes(post._id)}
                                onChange={(e) =>
                                  setSelected((ids) =>
                                    e.target.checked
                                      ? [...ids, post._id]
                                      : ids.filter((id) => id !== post._id)
                                  )
                                }
                              />
                            </td>
                            <td className="blog-article-cell">
                              <button
                                className="blog-title-button"
                                onClick={() => openEditor(post._id)}
                                disabled={busy || status === 'trash'}
                                dir={post.locale === 'ur' ? 'rtl' : 'ltr'}
                              >
                                {post.title}
                              </button>
                              <small>
                                {post.categories.map((t) => t.name).join(' · ') || 'Uncategorized'}{' '}
                                · {post.readingMinutes} min read · {post.viewCount} views
                              </small>
                            </td>
                            <td>
                              <span
                                className={
                                  'blog-status ' +
                                  (status === 'trash' ? 'trash' : post.effectiveStatus)
                                }
                              >
                                {status === 'trash' ? 'trash' : post.effectiveStatus}
                              </span>
                            </td>
                            <td>{post.author?.name || 'Editorial team'}</td>
                            <td>{new Date(post.updatedAt).toLocaleDateString()}</td>
                            <td>
                              <div className="blog-actions">
                                {status !== 'trash' && (
                                  <>
                                    <button
                                      className="blog-icon-button"
                                      aria-label={'Edit ' + post.title}
                                      disabled={busy}
                                      onClick={() => openEditor(post._id)}
                                    >
                                      <FiEdit2 />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  {loading && (
                    <div className="blog-empty" role="status">
                      Loading articles…
                    </div>
                  )}
                  {!loading && !result.posts.length && (
                    <div className="blog-empty">
                      <FiFileText size={32} />
                      <h3>
                        {query || status || category
                          ? 'No articles match these filters'
                          : 'Your next story starts here'}
                      </h3>
                      <p>Create an article, add its SEO details, and publish when it’s ready.</p>
                    </div>
                  )}
                </div>
                <Pagination page={page} totalPages={result.totalPages} setPage={setPage} />
              </section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
